import { Helmet } from 'react-helmet-async';

export type AppLocale = 'en_CA' | 'fr_CA';

type SeoAlternatePaths = Record<AppLocale, string>;

interface SeoLinksProps {
  canonicalPath: string
  alternatePaths: SeoAlternatePaths
  noIndex?: boolean
}

type SiteUrlGlobal = typeof globalThis & {
  SITE_URL?: string
}

const DEFAULT_SITE_URL = 'https://artofwhere.com/';

export const getCanonicalLocale = (locale: string | undefined): AppLocale => {
  const normalizedLocale = locale?.replace('_', '-').toLowerCase();

  return normalizedLocale === 'fr' || normalizedLocale?.startsWith('fr-') === true ? 'fr_CA' : 'en_CA';
}

export const getI18nextLocale = (locale: AppLocale): 'en-CA' | 'fr-CA' => {
  return locale.replace('_', '-') as 'en-CA' | 'fr-CA';
}

const getSiteUrl = () => {
  const siteUrl = typeof window !== 'undefined'
    ? window.SITE_URL
    : (globalThis as SiteUrlGlobal).SITE_URL;

  return (siteUrl ?? DEFAULT_SITE_URL).replace(/\/+$/, '');
}

const normalizePath = (path: string) => {
  if (path === '') {
    return '/';
  }

  return '/' + path.replace(/^\/+/, '').replace(/\/+$/, '');
}

export const joinSeoPath = (...parts: Array<string | null | undefined>) => {
  const path = parts
    .filter((part): part is string => part !== undefined && part !== null && part !== '')
    .map(part => part.replace(/^\/+/, '').replace(/\/+$/, ''))
    .join('/');

  return normalizePath(path);
}

export const buildAbsoluteSeoUrl = (path: string) => {
  return getSiteUrl() + normalizePath(path);
}

export default function SeoLinks(props: SeoLinksProps) {
  const canonicalUrl = buildAbsoluteSeoUrl(props.canonicalPath);
  const englishUrl = buildAbsoluteSeoUrl(props.alternatePaths.en_CA);
  const frenchUrl = buildAbsoluteSeoUrl(props.alternatePaths.fr_CA);

  return <Helmet>
    <link rel="canonical" href={canonicalUrl} />
    <link rel="alternate" href={englishUrl} hrefLang="x-default" />
    <link rel="alternate" href={englishUrl} hrefLang="en" />
    <link rel="alternate" href={frenchUrl} hrefLang="fr" />
    <meta property="og:url" content={canonicalUrl} />
    {props.noIndex ? <meta name="robots" content="noindex,follow" /> : null}
  </Helmet>
}
